<i18n>
{
  "en": {
    "productColName": "Product",
    "noRows": "No data is available with the current constraints, please either complete the filters or select others."
  },
  "es": {
    "productColName": "Producto",
    "noRows": "No hay datos disponibles para los filtros seleccionados, por favor, termina de seleccionar los filtros o cambia la selección de los mismos."
  }
}
</i18n>

<template>
  <component
    ref="revo"
    v-if="isLoaded"
    :is="vGrid"
    :source="rows"
    :columns="columns"
    theme="material"
    resize="true"
    @beforeedit="onBeforeEdit"
    @afteredit="onAfterEdit"
    @beforeeditstart="onBeforeEditStart"
    @beforerangeedit="onBeforeRangeEdit"
    range="true"
    class="mpdd-table"
    @headerclick="onHeaderClick"
    @rowdragstart="onRowDragStart"
    @roworderchanged="onRowOrderChanged"
    @beforeautofill="onBeforeAutoFill"
  />
  <div v-else-if="showNoRowsMessage" class="grey--text text-center">{{ $t("noRows") }}</div>

</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { MapActions, MapGetters } from '@core/helpers/decorators';
import { notValidNumber } from '@core/helpers/formValidators';
import DummyVGrid from '@dynameat/components/DummyVGrid.vue';
import { Prop, Watch } from 'vue-property-decorator';
import cloneDeep from 'lodash/cloneDeep';
import { EventBus } from '../../../helpers/eventBus';
import { FrontMenuProductDynamicDataList } from '../models';
import MenuCell from './MenuCell.vue';
import ProductCell from '../../../components/ProductCell.vue';

@Component
@MapActions(['undoPush', 'undoPop', 'undoClear'])
@MapGetters(['serviceList', 'groupList', 'menuFilterList'])
export default class MenuTable extends Vue {
  @Prop({ type: Object, default: () => ({}) }) frontMenuProductDynamicData;

  vGrid = null;

  VGridVueTemplate = (attr) => attr;

  validator = notValidNumber('error');

  today = new Date();

  stylesCache = new Map();

  rows = [];

  columns = [];

  // Extra styles for any regular cell.
  regularCell() {
    return {
      style: {
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
        'font-size': '15px'
      },
    };
  }

  coloredServiceCell(colorHex) {
    return {
      style: {
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
        'background-color': `${colorHex}`,
        'font-size': '15px'
      },
    };
  }

  // Extra styles for the cells that can not be edited.
  disabledCell() {
    return {
      style: {
        'background-color': '#f4f4f4',
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
        'font-style': 'italic',
        'pointer-events': 'none',
        color: '#747474'
      }
    };
  }

  lastRowRegular() {
    return {
      style: {
        'border-bottom': '#b4b4b4 solid 1px',
      }
    };
  }

  lastColumnRegular() {
    return {
      style: {
        'border-right': '#b4b4b4 solid 1px',
      }
    };
  }

  touchedCell() {
    return {
      style: {
        'background-color': 'rgb(254 246 214 / 58%)',
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
      },
      class: 'warning-cell'
    };
  }

  errorCell() {
    return {
      style: {
        'background-color': '#ff5e5e',
        'border-top': '#b4b4b4 solid 1px',
        'border-left': '#b4b4b4 solid 1px',
      },
      class: 'error-cell'
    };
  }

  // This properties will apply to the "2021-01-01" cells.
  mainColumnProps() {
    return {
      size: 300,
      columnProperties: ({ name }) => ({
        style: {
          'font-style': this.isEnabledCell(name, this.today) ? 'regular' : 'italic',
          'text-align': 'center',
          'background-color': this.$vuetify.theme.currentTheme.primary,
          color: 'white',
          'border-top': '#b4b4b4 solid 1px',
          'border-left': '#b4b4b4 solid 1px',
          'font-size': '18px'
        }
      }),
    };
  }

  draggedCol = null;

  get isLoaded() {
    return this.vGrid && this.frontMenuProductDynamicData instanceof FrontMenuProductDynamicDataList && this.table.rows.length > 0 && this.rows.length > 0;
  }

  // First column props, which corresponds to the product.
  get initialColumn() {
    return {
      name: this.$t('productColName'),
      prop: 'productName',
      pin: 'colPinStart',
      sortable: true,
      rowDrag: true,
      size: 300,
      cellTemplate: this.firstCellTemplate,
      cellProperties: ({ model }) => this.getStyleForCell()(null, model),
      columnProperties: () => this.getStyleForCell()(null, null),
    };
  }

  // The way of revo grid to dirty-mount a component for the cell.
  get cellTemplate() {
    return this.VGridVueTemplate(MenuCell);
  }

  get firstCellTemplate() {
    return this.VGridVueTemplate(ProductCell);
  }

  // Properties for both any cells except the product ones and for the header cells of "Comidas".
  get childColumnProps() {
    return {
      size: 150,
      cellTemplate: this.cellTemplate,
      rowDrag: true,
      cellProperties: ({ prop, model }) => this.getStyleForCell()(prop, model),
      columnProperties: ({ prop }) => this.getStyleForCell()(prop, null, true),
    };
  }

  get table() {
    let table;
    if (this.frontMenuProductDynamicData instanceof FrontMenuProductDynamicDataList) {
      table = this.frontMenuProductDynamicData.toTable(this.serviceList, this.mainColumnProps(), this.childColumnProps, this.initialColumn);
    } else {
      table = { columns: [], rows: [] };
    }
    this.rows = table.rows;
    this.columns = table.columns;
    return table;
  }

  get rangeSelector() {
    return this.$el.querySelector('.mpdd-table .rgCol revogr-overlay-selection[slot=content]');
  }

  get showNoRowsMessage() {
    return !this.isLoaded && this.menuFilterList.length;
  }

  getCellColor(prop) {
    const parts = prop.split('_');
    const date = parts[0];
    const serviceGuid = parts[1];
    const foundObject = this.frontMenuProductDynamicData.groupList.find((group) => group.serviceGuid === serviceGuid && group.date === date);
    if (foundObject) {
      const { codeName } = foundObject;
      const foundGroup = this.groupList.find((group) => group.code_name === codeName);
      if (foundGroup) {
        return foundGroup.color;
      }
    }
    return '#FFFFFF';
  }

  async created() {
    await this.undoClear();
    document.addEventListener('keydown', this.onKeyDown, true);
    document.addEventListener('keyup', this.onKeyUp, true);
  }

  mounted() {
    // This is needed in order to properly load the component. Revo-grid does some hacky things
    // to make itself compatible with every framework, and to ensure compatibility, we need to
    // lazy import and use <component>
    import('@revolist/vue-datagrid').then((m) => {
      this.vGrid = m.VGrid;
      this.VGridVueTemplate = m.VGridVueTemplate;
    }).catch(() => {
      this.vGrid = DummyVGrid;
      this.VGridVueTemplate = (attr) => attr;
    });

    EventBus.$on('special', this.onSpecial);
    EventBus.$on('isVisible', this.onIsVisible);
  }

  destroyed() {
    document.removeEventListener('keydown', this.onKeyDown, true);
    document.removeEventListener('keyup', this.onKeyUp, true);

    EventBus.$off('special', this.onSpecial);
    EventBus.$off('isVisible', this.onIsVisible);
  }

  isErrorCell(prop, model) {
    const currentOrders = this.rows.map((row) => parseInt(row[prop], 10));
    const value = parseInt(model[prop], 10);
    const currentOrdersSet = new Set(currentOrders);
    const duplicates = new Set(currentOrders.filter((item) => {
      if (currentOrdersSet.has(item)) {
        currentOrdersSet.delete(item);
        return false;
      }
      return true;
    }));
    return duplicates.has(value);
  }

  isTouchedCell(prop, model) {
    const newOrder = model[prop];
    const mpddGuid = model[`${prop}_guid`];
    const newSpecial = model[`${prop}_special`];
    const newVisible = model[`${prop}_is_visible`];
    return this.frontMenuProductDynamicData.hasMpddChanged(mpddGuid, newOrder, newSpecial, newVisible);
  }

  isEnabledCell(prop, today) {
    // The cell is only enabled in the future and in the future services of today.
    const parts = prop.split('_');
    const day = new Date(parts[0]);
    const isFuture = today < day;
    if (isFuture) {
      return true;
    }
    const isToday = today.toDateString() === day.toDateString();
    if (isToday) {
      const serviceFuture = this.serviceList.some((service) => service.isBeforeTime(today));
      if (parts.length === 1) {
        return serviceFuture;
      }
      const servicePresent = this.serviceList.find((service) => service.guid === parts[1])?.isBetweenTime(today);
      return serviceFuture || servicePresent;
    }
    return false;
  }

  isLastRowCell(model) {
    return this.rows.findIndex((row) => row.menuProductGuid === model.menuProductGuid) === this.rows.length - 1;
  }

  isLastColumnCell(prop) {
    const columns = this.columns.filter((col) => col.children).flatMap((col) => col.children.map((child) => child.prop));
    return columns.findIndex((column) => column === prop) === columns.length - 1;
  }

  isLastColumnHeader(prop) {
    const parts = prop.split('_');
    let res = true;
    if (parts.length > 1) {
      res = res && this.isLastColumnCell(prop);
    }
    const header = parts[0];
    const columns = this.columns.map((col) => col.name);
    return res && columns.findIndex((column) => column === header) === columns.length - 1;
  }

  lastPositionCell(prop, model, currentStyle) {
    if (model) {
      if (this.isLastRowCell(model)) {
        const lastRowStyle = this.lastRowRegular();
        currentStyle.style = { ...currentStyle.style, ...lastRowStyle.style };
      }
      if (prop && this.isLastColumnCell(prop)) {
        const lastColumnStyle = this.lastColumnRegular();
        currentStyle.style = { ...currentStyle.style, ...lastColumnStyle.style };
      }
    } else if (prop && this.isLastColumnHeader(prop)) {
      const lastColumnStyle = this.lastColumnRegular();
      currentStyle.style = { ...currentStyle.style, ...lastColumnStyle.style };
    }
    return currentStyle;
  }

  getStyleForCell() {
    // This is a function that returns a function. It is used to bind the parameters to the function,
    // so they are not always accessed from the vue instance.
    return (prop, model, header = false) => {
      const key = `${prop || 'default'}_${model ? model.menuProductGuid : 'default'}`;
      if (!this.stylesCache.has(key)) {
        // The inside function returns the styles of the cell based on the prop.
        let result;
        // Header, for using the demand level and color it.
        if (header) {
          result = this.coloredServiceCell(this.getCellColor(prop));
        } else {
          if (prop) {
            if (model && this.isErrorCell(prop, model)) {
              result = this.errorCell();
              // If the cell is from today.
            } else if (this.isEnabledCell(prop, this.today)) {
              if (this.isTouchedCell(prop, model)) {
                result = this.touchedCell();
              } else result = this.regularCell();
            } else {
              result = this.disabledCell();
            }
          } else {
            result = this.regularCell();
          }
          result = this.lastPositionCell(prop, model, result);
        }
        this.stylesCache.set(key, result);
      }
      return this.stylesCache.get(key);
    };
  }

  invalidateColumnCache(column) {
    const keysToDelete = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const key of this.stylesCache.keys()) {
      const columnKey = key.split('_').splice(0, 2).join('_');
      if (column === columnKey) {
        keysToDelete.push(key);
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const key of keysToDelete) {
      this.stylesCache.delete(key);
    }
    this.$refs?.revo?.$el?.refresh();
  }

  onAfterEdit({ detail }) {
    // Send the changes so they are queued.
    const changes = [];
    // Single edit model
    if (detail.model) {
      changes.push(this.getOrderChange(detail.model, detail.prop, detail.val));
      this.invalidateColumnCache(detail.prop);
      // In multi edit mode, we extract the changes which are nested in the data.
    } else if (detail.models) {
      Object.keys(detail.data).forEach((row) => {
        const model = detail.models[row];
        Object.keys(detail.data[row]).forEach((prop) => {
          const val = detail.data[row][prop];
          changes.push(this.getOrderChange(model, prop, val));
          this.invalidateColumnCache(prop);
        });
      });
    }

    this.$emit('mpdd-order-changed', changes);
  }

  getOrderChange(model, prop, value) {
    return {
      mpddGuid: model[`${prop}_guid`],
      newOrder: value
    };
  }

  onSpecial({
    mpddGuid, newValue, prop
  }) {
    if (this.isEnabledCell(prop, this.today)) {
      this.invalidateColumnCache(prop);
      const oldTable = cloneDeep({ rows: this.rows, columns: this.columns, event: 'undo-special' });
      oldTable.rows.find((row) => row[`${prop}_guid`] === mpddGuid)[`${prop}_special`] = !newValue;
      this.undoPush(oldTable);
      this.$emit('mpdd-special-changed', { mpddGuid, newValue });
    }
  }

  onIsVisible({ mpddGuid, newValue, prop }) {
    if (this.isEnabledCell(prop, this.today)) {
      this.invalidateColumnCache(prop);
      const oldTable = cloneDeep({
        rows: this.rows,
        columns: this.columns,
        event: 'undo-is-visible'
      });
      oldTable.rows.find((row) => row[`${prop}_guid`] === mpddGuid)[`${prop}_is_visible`] = !newValue;
      this.undoPush(oldTable);
      this.$emit('mpdd-is-visible-changed', { mpddGuid, newValue });
    }
  }

  onBeforeEditStart(event) {
    // To disable the edition of disabled cells.
    if (!this.isEnabledCell(event.detail.prop, this.today)) {
      event.preventDefault();
    }
  }

  onBeforeEdit(event) {
    // This will not save the data if its invalid.
    if (this.validator(event.detail.val) !== true) {
      event.preventDefault();
    } else {
      event.detail.val = Number.parseFloat(event.detail.val);
      this.undoPush(cloneDeep({
        rows: this.rows, columns: this.columns, event: 'undo-order', eventArgs: [1]
      }));
    }
  }

  onBeforeRangeEdit(event) {
    // Bulk edit mode, we need to filter out the changes that will otherwise change disabled cells.
    // For that, we delete the changes and if there is no change left, we prevent the event.
    const { detail } = event;
    const keysToDelete = [];
    Object.keys(detail.data).forEach((row) => {
      Object.keys(detail.data[row]).forEach((prop) => {
        if (!this.isEnabledCell(prop, this.today)) {
          keysToDelete.push({ row, prop });
        }
      });
    });
    keysToDelete.forEach((deleteAble) => {
      delete detail.data[deleteAble.row][deleteAble.prop];
      if (Object.keys(detail.data[deleteAble.row]).length === 0) {
        delete detail.data[deleteAble.row];
      }
    });
    if (Object.keys(detail.data).length === 0) {
      event.preventDefault();
    } else {
      let numberOfChanges = 0;
      Object.keys(detail.data).forEach((row) => {
        Object.keys(detail.data[row]).forEach(() => {
          numberOfChanges += 1;
        });
      });
      this.undoPush(cloneDeep({
        rows: this.rows,
        columns: this.columns,
        event: 'undo-order',
        eventArgs: [numberOfChanges]
      }));
    }
  }

  onHeaderClick({ detail }) {
    const columnIndex = this.columns.filter((col) => col.children).flatMap((col) => col.children.map((child) => child.prop)).findIndex((prop) => prop === detail.prop);
    const rows = this.rows.length - 1;
    this.rangeSelector?.selectionStoreService?.focus({
      x: columnIndex,
      y: 0
    }, detail.originalEvent.shiftKey);
    this.rangeSelector?.selectionStoreService?.focus({ x: columnIndex, y: rows }, true);
  }

  onBeforeAutoFill(e) {
    e.preventDefault();
  }

  onKeyUp(e) {
    if (e.key === 'z' && e.ctrlKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
    }
  }

  onKeyDown(e) {
    if (e.key === 'z' && e.ctrlKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      this.undo();
    }
  }

  async undo() {
    const oldTable = await this.undoPop();
    if (oldTable) {
      this.columns = oldTable.columns;
      this.rows = oldTable.rows;
      this.stylesCache.clear();
      this.$emit(oldTable.event, oldTable.eventArgs);
    }
  }

  onRowDragStart(e) {
    // When the drag start, we store the source column, as we later wont have access to it.
    this.draggedCol = this.columns.filter((col) => col.children).flatMap((col) => col.children.map((child) => child.prop))[e.detail.cell.x];
    if (!this.isEnabledCell(this.draggedCol, this.today)) {
      e.preventDefault();
      this.draggedCol = null;
    }
  }

  onRowOrderChanged(e) {
    // We stop the propagation, as we do not really complete the drag and drop, we just change the
    // Values.
    e.preventDefault();
    if (this.draggedCol) {
      const oldTable = cloneDeep({
        rows: this.rows, columns: this.columns, event: 'undo-order', eventArgs: []
      });
      // We can only move up and down, i.e, in the same column
      // There is a bug in the drag and drop, where it is one lower than it should be when going up.
      // This solves it.
      const increment = e.detail.from === e.detail.to ? -1 : 0;
      // Clip it between 0 and the number of rows.
      const newDestination = Math.min(Math.max(e.detail.to + increment, 0), this.rows.length - 1);
      const originalValue = this.rows[e.detail.from][this.draggedCol];
      this.rows[e.detail.from][this.draggedCol] = this.rows[newDestination][this.draggedCol];
      this.rows[newDestination][this.draggedCol] = originalValue;
      this.invalidateColumnCache(this.draggedCol);

      const changes = [];
      changes.push(this.getOrderChange(this.rows[e.detail.from], this.draggedCol, this.rows[e.detail.from][this.draggedCol]));
      changes.push(this.getOrderChange(this.rows[newDestination], this.draggedCol, this.rows[newDestination][this.draggedCol]));
      oldTable.eventArgs.push(changes.length);
      this.undoPush(oldTable);
      this.$emit('mpdd-order-changed', changes);
    }
    this.draggedCol = null;
  }

  @Watch('frontMenuProductDynamicData', { immediate: true })
  onFrontMenuProductDynamicData() {
    this.stylesCache.clear();
  }
}

</script>

<style lang="scss">
/*This hacks are needed to disable the default lines, which messes with performance and is harder to
extend.*/
.mpdd-table .colPinStart {
  box-shadow: 0 0 0 #ffffff inset !important;
}

.mpdd-table .rgRow {
  box-shadow: 0 0 0 #ffffff inset !important;
}

.mpdd-table .header-rgRow {
  box-shadow: 0 0 0 #ffffff inset !important;
  text-align: center;
}

.mpdd-table revogr-temp-range.temp-bg-range.Selection {
  border: 2px dashed gray;
  display: block;
}

.mpdd-table revogr-overlay-selection .focused-cell {
  box-shadow: 0 0 0 #ffffff inset !important;
}

.mpdd-table revogr-overlay-selection .selection-border-range {
  box-shadow: 0 0 0 #ffffff inset !important;
  border: #0d63e8 solid 2px;
}
</style>
